import React from 'react';
import { useState, useEffect, useRef } from 'react';
import './classes/ReferralRegistrationForm.css';
import { configData } from "../config";
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import ReCAPTCHA from "react-google-recaptcha";
import { isBlockedEmail } from "./services/QuickAppService";
import { loadCountryStateLists } from "./services/CountryStateLists";

// The sign-up form the live site shows in place of {{REGISTRATION}} on the referral pages: the
// visitor fills it in to get their referral code. Drupal's smww_node_view() (smww.module) picked
// the form by node, from referrer.inc.php; here the server puts the mount point
// <div data-type="referral-registration" data-form="..."> there and Body.js renders this on it.
//   general  - /refer_friends (254318), "Refer Friends Now!"
//   referrer - /refer_friends1 (285819), "Create Your Referral Code"
//   nil      - /nil_refer_friends (243271), "Make Money Now!", the NIL athlete form
// The same fields, ids and messages as the Drupal forms. The first two post to
// referral/publicreferralregistration (the API the Drupal site forwarded /refer_friends to, and
// did the same as itself for /refer_friends1), the NIL form to referral/athletereferralregistration.
// The API makes the visitor a Referrer (or NIL Athlete), which creates their referral code
// (coupon), and emails them.
export const REFERRAL_FORM_PAGES = { 254318: 'general', 285819: 'referrer', 243271: 'nil' };

const HEADINGS = {
    general: 'Refer Friends Now!',
    referrer: 'Create Your Referral Code',
    nil: 'Make Money Now!',
};

const ENDPOINTS = {
    general: 'referral/publicreferralregistration',
    referrer: 'referral/publicreferralregistration',
    nil: 'referral/athletereferralregistration',
};

// The NIL form's sports as the Drupal form listed them (ids in smww_sports)
const NIL_SPORTS = [
    ['45', 'Action Sports'], ['2', 'Baseball'], ['3', 'Basketball'], ['7', 'Boxing'], ['8', 'Cricket'],
    ['9', 'Curling'], ['42', 'Extreme Sports'], ['1', 'Football (American)'], ['5', 'Golf'],
    ['6', 'Hockey'], ['39', 'Motor Sports'], ['44', 'Olympic'], ['41', 'Other'], ['16', 'Polo'],
    ['18', 'Rodeo'], ['46', 'Rugby'], ['4', 'Soccer'], ['30', 'Tennis'], ['31', 'Track and Field'],
];

// isEmail() in the Drupal forms
const EMAIL = /^([A-Za-z0-9_\-.])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,4})$/;

const emptyForm = {
    fname: '',
    lname: '',
    email: '',
    emailConfirm: '',
    phone: '',
    fteam: '',
    fcity: '',
    fcountry: 'US',
    fstate: '',
    fprovince: '',
    school: '',
    sport: '',
    age: '',
    instagram_handle: '',
    tik_tok_handle: '',
    twitter_handle: '',
    youtube_handle: '',
    facebook_handle: '',
    linkedin_handle: '',
    confirm: false,
};

// A state is asked for in the US and Canada, a province everywhere else
const asksState = (country) => country === 'US' || country === 'CA';

const blockedEmailMessage = (email) => (
    email.trim().toLowerCase() === 'info@smww.com'
        ? 'Please enter your email address (cannot be info@smww.com)'
        : 'Please enter your email address.'
);

// The first problem found, in the order and words of the Drupal forms' checks
const validate = (form, data, captcha) => {
    if (!data.fname.trim()) return 'Please enter your first name.';
    if (!data.lname.trim()) return 'Please enter your last name.';
    if (!EMAIL.test(data.email)) return 'Please enter a valid email address.';
    if (isBlockedEmail(data.email)) return blockedEmailMessage(data.email);
    if (form !== 'general') {
        if (!EMAIL.test(data.emailConfirm)) return 'Please enter a valid email address.';
        if (data.email !== data.emailConfirm) return 'The emails do not match.';
    }
    if (!data.phone || !isValidPhoneNumber(data.phone)) return 'Please fill in valid primary phone number.';
    if (form === 'nil') {
        if (!data.school.trim()) return 'Please enter your School.';
        if (!data.sport) return 'Please select your Sport.';
        if (!String(data.age).trim()) return 'Please enter your Age.';
        if (data.linkedin_handle && !data.linkedin_handle.toLowerCase().includes('linkedin.com/')) {
            return 'Please enter a valid LinkedIn URL.';
        }
        if (!data.confirm) return 'If you are not NCAA or NAIA athlete, then you are not eligible to submit this form.';
    } else {
        if (!data.fteam.trim()) return 'Please enter your favourite team.';
        if (!data.fcountry) return 'Please Select Country';
        // The general form asked for a state in the US only, the other one in Canada too
        const stateNeeded = form === 'general' ? data.fcountry === 'US' : asksState(data.fcountry);
        if (stateNeeded && !data.fstate) return 'Please Select State';
        if (!data.fcity.trim()) return 'Please enter your city.';
    }
    if (!captcha) return "Please verify you're not a robot.";
    return '';
};

// The fields the Drupal form posted (its serialized form plus pageURL)
const postedFields = (form, data, captcha) => {
    const fields = {
        fname: data.fname,
        lname: data.lname,
        email: data.email,
        phone: data.phone,
        full_phone: data.phone,
        'g-recaptcha-response': captcha,
        pageURL: window.location.href,
    };
    if (form !== 'general') fields.emailConfirm = data.emailConfirm;
    if (form === 'nil') {
        Object.assign(fields, {
            school: data.school,
            sport: data.sport,
            age: data.age,
            instagram_handle: data.instagram_handle,
            tik_tok_handle: data.tik_tok_handle,
            twitter_handle: data.twitter_handle,
            youtube_handle: data.youtube_handle,
            facebook_handle: data.facebook_handle,
            linkedin_handle: data.linkedin_handle,
            confirm: 'Yes',
        });
    } else {
        Object.assign(fields, {
            fteam: data.fteam,
            fcity: data.fcity,
            fcountry: data.fcountry,
            fstate: asksState(data.fcountry) ? data.fstate : '',
            fprovince: asksState(data.fcountry) ? '' : data.fprovince,
        });
    }
    return fields;
};

const Text = ({ id, name, label, value, onChange, type = 'text', onBlur, children }) => (
    <div className="col-md-6">
        <label htmlFor={id}>{label}</label><br />
        <input className="form-control" id={id} name={name} placeholder="" type={type} value={value} onChange={onChange} onBlur={onBlur} />
        {children}
    </div>
);

const ReferralRegistrationForm = ({ form = 'general' }) => {
    const recaptchaRef = useRef(null);
    const [formData, setFormData] = useState(emptyForm);
    const [lists, setLists] = useState({ countries: [], states: [] });
    const [sending, setSending] = useState(false);
    const [thanks, setThanks] = useState(false);
    const [emailsDiffer, setEmailsDiffer] = useState(false);

    useEffect(() => {
        if (form !== 'nil') loadCountryStateLists().then(setLists);
    }, [form]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else if (name === 'fcountry') {
            setFormData((prev) => ({ ...prev, fcountry: value, fstate: '', fprovince: '' }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    // confirmEmail() in the Drupal form: leaving the second email field says when it differs
    const checkEmails = () => {
        if (formData.emailConfirm && formData.email !== formData.emailConfirm) {
            setEmailsDiffer(true);
            alert('The emails do not match.');
        } else {
            setEmailsDiffer(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (sending) return;
        const captcha = recaptchaRef.current?.getValue() || '';
        const message = validate(form, formData, captcha);
        if (message) {
            alert(message);
            return;
        }

        setSending(true);
        fetch(configData.APP_URL + ENDPOINTS[form], {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams(postedFields(form, formData, captcha)).toString(),
        })
            .then((response) => response.json())
            .then((data) => {
                const result = data?.data?.result;
                if (result === 'user_created') {
                    setThanks(true);
                    return;
                }
                throw new Error(result || data?.message || 'not saved');
            })
            .catch((err) => {
                console.error(err);
                recaptchaRef.current?.reset();
                alert(err.message === 'captcha_failed'
                    ? "Please verify you're not a robot."
                    : "We couldn't send that. Please try again.");
            })
            .finally(() => setSending(false));
    };

    const stateCountry = asksState(formData.fcountry);
    // The /refer_friends1 form listed the states of the chosen country alone (Drupal's getstateList)
    const states = form === 'referrer'
        ? lists.states.filter((state) => !state.country || state.country === formData.fcountry)
        : lists.states;

    const email = (
        <Text id="email" name="email" label="Email" value={formData.email} onChange={handleChange} />
    );
    const emailConfirm = (
        <div className="col-md-6">
            <label htmlFor="confemail">Email, again</label><br />
            <input className="form-control" id="confemail" name="emailConfirm" placeholder="" type="text"
                value={formData.emailConfirm} onChange={handleChange} onBlur={checkEmails}
                style={emailsDiffer ? { border: '1px solid red' } : undefined} />
            <span style={{ fontSize: 12 }}>Please confirm your email address by entering it again.</span>
        </div>
    );
    const phone = (
        <div className="col-md-6">
            <label htmlFor="fphone">Phone</label><br />
            <PhoneInput
                id="fphone"
                className="form-control referral-registration__phone"
                international
                countryCallingCodeEditable={false}
                defaultCountry="US"
                value={formData.phone}
                onChange={(value) => setFormData((prev) => ({ ...prev, phone: value || '' }))}
            />
        </div>
    );
    const team = (
        <Text id="fteam" name="fteam" label="Favorite Team or Club?" value={formData.fteam} onChange={handleChange} />
    );
    const city = (
        <Text id="fcity" name="fcity" label="City" value={formData.fcity} onChange={handleChange} />
    );
    const country = (
        <div className="col-md-6">
            <label htmlFor="fcountry">Country</label><br />
            <select className="form-control" id="fcountry" name="fcountry" size="1" value={formData.fcountry} onChange={handleChange}>
                {lists.countries.length === 0 && <option value="US">United States</option>}
                {lists.countries.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                ))}
            </select>
        </div>
    );
    const stateOrProvince = stateCountry ? (
        <div className="col-md-6" id="fstate">
            <label htmlFor="state">State</label><br />
            <select className="form-control" name="fstate" id="state" size="1" value={formData.fstate} onChange={handleChange}>
                {form === 'general' ? (
                    <>
                        <option value="">Select a State or Province</option>
                        <option value="-">Outside the US or Canada</option>
                    </>
                ) : (
                    <option value="">-Select State/Province-</option>
                )}
                {states.map((state) => (
                    <option key={state.abbreviation} value={state.abbreviation}>{state.name}</option>
                ))}
            </select>
        </div>
    ) : (
        <div className="col-md-6" id="fprovince">
            <label htmlFor="province">Province</label><br />
            <input className="form-control" name="fprovince" id="province" placeholder="" type="text" value={formData.fprovince} onChange={handleChange} />
        </div>
    );

    let rows;
    if (form === 'general') {
        rows = (
            <>
                <div className="row">
                    <Text id="fname" name="fname" label="First Name" value={formData.fname} onChange={handleChange} />
                    <Text id="lname" name="lname" label="Last Name" value={formData.lname} onChange={handleChange} />
                </div>
                <div className="row">{email}{phone}</div>
                <div className="row">{team}{city}</div>
                <div className="row">{country}{stateOrProvince}</div>
            </>
        );
    } else if (form === 'referrer') {
        rows = (
            <>
                <div className="row">
                    <Text id="fname" name="fname" label="First Name" value={formData.fname} onChange={handleChange} />
                    <Text id="lname" name="lname" label="Last Name" value={formData.lname} onChange={handleChange} />
                </div>
                <div className="row">{email}{emailConfirm}</div>
                <div className="row third-row">{phone}{team}</div>
                <div className="row">{city}{country}</div>
                <div className="row">{stateOrProvince}</div>
            </>
        );
    } else {
        rows = (
            <>
                <div className="row">
                    <Text id="fname" name="fname" label="First Name" value={formData.fname} onChange={handleChange} />
                    <Text id="lname" name="lname" label="Last Name" value={formData.lname} onChange={handleChange} />
                </div>
                <div className="row">{email}{emailConfirm}</div>
                <div className="row third-row">
                    {phone}
                    <Text id="school" name="school" label="School" value={formData.school} onChange={handleChange} />
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <label htmlFor="sport">Sport</label><br />
                        <select className="form-control" name="sport" id="sport" value={formData.sport} onChange={handleChange}>
                            <option value="">Select</option>
                            {NIL_SPORTS.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                        </select>
                    </div>
                    <Text id="age" name="age" label="Age" type="number" value={formData.age} onChange={handleChange} />
                </div>
                <div className="row">
                    <Text id="instagram_handle" name="instagram_handle" label="Instagram Handle" value={formData.instagram_handle} onChange={handleChange} />
                    <Text id="tik_tok_handle" name="tik_tok_handle" label="TikTok Handle" value={formData.tik_tok_handle} onChange={handleChange} />
                </div>
                <div className="row">
                    <Text id="twitter_handle" name="twitter_handle" label="Twitter Handle" value={formData.twitter_handle} onChange={handleChange} />
                    <Text id="youtube_handle" name="youtube_handle" label="Youtube Handle" value={formData.youtube_handle} onChange={handleChange} />
                </div>
                <div className="row">
                    <Text id="facebook_handle" name="facebook_handle" label="Facebook Handle" value={formData.facebook_handle} onChange={handleChange} />
                    <Text id="linkedin_handle" name="linkedin_handle" label="LinkedIn URL" value={formData.linkedin_handle} onChange={handleChange} />
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <input type="checkbox" id="confirm" name="confirm" value="Yes" checked={formData.confirm} onChange={handleChange} />
                        <label htmlFor="confirm">  *Confirm you are a current NCAA or NAIA athlete. </label><br />
                    </div>
                </div>
            </>
        );
    }

    return (
        <div id="registration-form" className={'referral-registration referral-registration--' + form}>
            <div id="open-registrations">
                <div className="jumbotron">
                    <div className="registration_open">{HEADINGS[form]}</div>
                </div>
            </div>

            {thanks ? (
                <div className="free-reg-dp-form-wrapper">Thank you for your interest!<br /> Someone from SMWW will be contacting you soon.</div>
            ) : (
                <form action="" className="free-reg-dp-form" id="free-reg-dp-form" method="post" name="free-reg-dp-form" onSubmit={handleSubmit} noValidate>
                    {rows}
                    <div className="row">
                        <div className="col-md-12">
                            <ReCAPTCHA ref={recaptchaRef} className="g-recaptcha" sitekey={configData.GOOGLE_RECAPTCHA_SITE_KEY} />
                        </div>
                    </div>

                    <div className="text-right" style={{ textAlign: 'right', paddingTop: 20 }}>
                        <button type="submit" className="btn btn-info font-bold btn-lg" id="new-jobs-form-submit" disabled={sending}>Submit</button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default ReferralRegistrationForm;
